// public/js/modules/map/clustering.js
// MapLibre-native clustering for the beta map.
// When clustering is enabled we use a GeoJSON source + layers (very efficient).
// When disabled we fall back to the individual rich DOM markers.

import { markerClusterer, setMarkerClusterer, markers, currentFilteredAssets, mapInstance } from './state.js';
import { mapFilterManager } from '../filters/filter-manager.js';
import { typeColors, LABEL_FONT, isValidLatLon, CLUSTER_MIN_ASSETS } from './constants.js';
import { addAssetMarker, showAssetPopup } from './markers.js';
import { loadAssetHistoryAndDrawRoute } from './history.js';
import { formatAssetLabel } from '../../utils/asset-utils.js';

let clusterSourceId = 'asset-clusters';
let clusterLayerId = 'clusters';
let clusterCountLayerId = 'cluster-count';
let unclusteredLayerId = 'unclustered-points';

const LABEL_BG_IMAGE = 'asset-label-pill';

// Creates a dark rounded-pill background image for the point labels, so they're
// legible over any base map. Registered as a 9-patch (stretchable) image: only
// the middle is stretched to fit the text, the rounded corners stay intact.
// setStyle() drops registered images, so we (re-)add it on each applyClustering.
function ensureLabelBgImage(map) {
  try {
    if (map.hasImage && map.hasImage(LABEL_BG_IMAGE)) return;
  } catch (e) {}

  const PR = 2;                 // pixelRatio for crisp corners
  // Keep the fixed (corner) regions small so the pill doesn't distort when
  // icon-text-fit shrinks it to the small label text.
  const W = 24 * PR, H = 18 * PR, R = 6 * PR;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgba(20,20,20,0.82)';
  ctx.beginPath();
  ctx.moveTo(R, 0);
  ctx.arcTo(W, 0, W, H, R);
  ctx.arcTo(W, H, 0, H, R);
  ctx.arcTo(0, H, 0, 0, R);
  ctx.arcTo(0, 0, W, 0, R);
  ctx.closePath();
  ctx.fill();

  try {
    map.addImage(LABEL_BG_IMAGE, ctx.getImageData(0, 0, W, H), {
      pixelRatio: PR,
      stretchX: [[R, W - R]],
      stretchY: [[R, H - R]],
      content: [R, R, W - R, H - R]
    });
  } catch (e) {
    console.warn('[map] could not add label background image', e);
  }
}

export function destroyClusterLayers(map) {
  if (!map) map = mapInstance;
  if (!map) return;

  const idsToRemove = [
    clusterLayerId,
    clusterCountLayerId,
    unclusteredLayerId,
    'unclustered-labels'
  ];

  idsToRemove.forEach(id => {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch (e) {}
  });

  try {
    if (map.getSource(clusterSourceId)) map.removeSource(clusterSourceId);
  } catch (e) {}

  // NOTE: we intentionally do NOT detach the click/hover handlers here.
  // They are bound to stable layer *names* and attached exactly once per map
  // instance (see map.__mapClusterHandlers below), so they keep working after
  // the source/layers are destroyed and recreated. Detaching + reattaching on
  // every re-render was leaking a new global map.on('click') listener each time.
  setMarkerClusterer(null);
}

export async function applyClustering(map, assetsForClustering) {
  if (!map) return;

  destroyClusterLayers(map);

  const clusteringState = mapFilterManager.getState().clustering || { enabled: true };
  if (!clusteringState.enabled) {
    console.log('[map] Clustering disabled via filter');
    return;
  }

  const points = (assetsForClustering || currentFilteredAssets || [])
    .filter(a => isValidLatLon(a.lastLat, a.lastLon));

  // Second guard on the SAME constant the renderer uses. These two used to
  // carry independent literals, so a renderer that chose the clustering path
  // could land here and return in silence, leaving the map blank.
  if (points.length < CLUSTER_MIN_ASSETS) {
    console.log(`[map] Too few assets for clustering (${points.length})`);
    return;
  }

  // Build a GeoJSON FeatureCollection
  const features = points.map(asset => {
    const color = typeColors[asset.type] || typeColors.default;
    const displayLabel = formatAssetLabel(asset);
    const shortLabel = displayLabel.length > 14 ? displayLabel.substring(0, 13) + '…' : displayLabel;

    return {
      type: 'Feature',
      properties: {
        assetUUID: asset.assetUUID,
        label: shortLabel,
        color,
        type: asset.type || 'other'
      },
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(asset.lastLon), parseFloat(asset.lastLat)]
      }
    };
  });

  const geojson = {
    type: 'FeatureCollection',
    features
  };

  // Add source with clustering enabled
  map.addSource(clusterSourceId, {
    type: 'geojson',
    data: geojson,
    cluster: true,
    clusterMaxZoom: 14,
    clusterRadius: 38   // smaller → clusters break into individual pins sooner
  });

  // Cluster circles
  map.addLayer({
    id: clusterLayerId,
    type: 'circle',
    source: clusterSourceId,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#3b82f6',
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        16, 10, 20, 25, 26
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff'
    }
  });

  // Cluster count labels
  map.addLayer({
    id: clusterCountLayerId,
    type: 'symbol',
    source: clusterSourceId,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': LABEL_FONT,
      'text-size': 12
    },
    paint: {
      'text-color': '#fff'
    }
  });

  // Unclustered individual points (simple colored circles)
  map.addLayer({
    id: unclusteredLayerId,
    type: 'circle',
    source: clusterSourceId,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': ['get', 'color'],
      'circle-radius': 6,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff'
    }
  });

  // Label pills underneath the unclustered circles (dark rounded background +
  // white text, like the DOM markers). The background is a stretchable image
  // fitted to the text via icon-text-fit. Scales to 100k+ points (WebGL).
  ensureLabelBgImage(map);
  map.addLayer({
    id: 'unclustered-labels',
    type: 'symbol',
    source: clusterSourceId,
    filter: ['!', ['has', 'point_count']],
    minzoom: 11,  // labels appear when zoomed in enough (less clutter at overview) — must be at layer root, not inside layout!
    layout: {
      'text-field': ['get', 'label'],
      'text-font': LABEL_FONT,
      'text-size': 9,
      'text-offset': [0, 1.8],
      'text-anchor': 'top',
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      // Background pill, sized to the text
      'icon-image': LABEL_BG_IMAGE,
      'icon-text-fit': 'both',
      'icon-text-fit-padding': [2, 6, 2, 6],
      'icon-allow-overlap': false,
      'icon-ignore-placement': false
    },
    paint: {
      'text-color': '#ffffff'
    }
  });

  // Attach interaction listeners only once per map instance (prevents duplicate
  // handlers/listener leaks across re-renders and clustering toggles).
  // The handlers are bound to layer *names*, which we reuse, so they keep
  // working after destroy/recreate.
  if (!map.__mapClusterHandlers) {
    // Clicking a cluster should "open it up" so the children fill the viewport.
    // We fit the bounds of the cluster's actual leaves; if they're all stacked
    // on (nearly) the same spot we fall back to the expansion zoom.
    // NOTE: in MapLibre GL JS v4+ getClusterExpansionZoom / getClusterLeaves
    // return a Promise (the old Mapbox-style callback signature is gone). The
    // previous callback version silently never fired — that was the "cluster
    // click does nothing" bug.
    const fitToExpansionZoom = async (src, clusterId, coords) => {
      try {
        const zoom = await src.getClusterExpansionZoom(clusterId);
        map.easeTo({ center: coords, zoom: Math.min(zoom + 1, 20), duration: 450 });
      } catch (e) {
        console.warn('[map] getClusterExpansionZoom failed', e);
      }
    };

    const doClusterZoom = async (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [clusterLayerId, clusterCountLayerId]
      });
      if (!features.length) return false;

      const f = features[0];
      const clusterId = f.properties.cluster_id;
      const coords = f.geometry.coordinates;
      const src = map.getSource(clusterSourceId);
      if (!src) return false;

      try {
        // Pull every leaf (asset) under this cluster and frame them all so the
        // children fill the viewport.
        const leaves = await src.getClusterLeaves(clusterId, Infinity, 0);
        if (!leaves || !leaves.length) {
          await fitToExpansionZoom(src, clusterId, coords);
          return true;
        }

        const bounds = new maplibregl.LngLatBounds();
        leaves.forEach(l => bounds.extend(l.geometry.coordinates));

        // If all leaves are essentially on the same point, fitBounds would jump
        // to maxZoom and re-cluster immediately — use expansion zoom instead.
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const degenerate =
          Math.abs(ne.lng - sw.lng) < 1e-6 && Math.abs(ne.lat - sw.lat) < 1e-6;

        if (degenerate) {
          await fitToExpansionZoom(src, clusterId, coords);
        } else {
          map.fitBounds(bounds, { padding: 80, maxZoom: 18, duration: 500 });
        }
      } catch (err) {
        console.warn('[map] cluster expand failed, falling back', err);
        await fitToExpansionZoom(src, clusterId, coords);
      }
      return true;
    };

    // Reliable cluster click using map-level click + queryRenderedFeatures.
    map.on('click', (e) => {
      doClusterZoom(e);
    });

    // Extra: direct layer click on count labels (very reliable for clicking the numbers)
    map.on('click', clusterCountLayerId, (e) => {
      doClusterZoom(e);
    });

    // Change cursor on clusters (layer-specific hover is fine)
    map.on('mouseenter', clusterLayerId, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', clusterLayerId, () => { map.getCanvas().style.cursor = ''; });

    // Cursor + click on individual unclustered points (the colored circles)
    map.on('mouseenter', unclusteredLayerId, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', unclusteredLayerId, () => { map.getCanvas().style.cursor = ''; });

    map.on('click', unclusteredLayerId, (e) => {
      const f = e.features && e.features[0];
      if (!f) return;
      const uuid = f.properties.assetUUID;
      if (!uuid) return;

      const asset = currentFilteredAssets.find(a => a.assetUUID === uuid);
      if (asset) {
        showAssetPopup(asset, map);

        const lngLat = [parseFloat(asset.lastLon), parseFloat(asset.lastLat)];
        map.flyTo({ center: lngLat, zoom: Math.max(map.getZoom(), 13), duration: 400 });
      }
    });

    map.__mapClusterHandlers = true;
  }

  setMarkerClusterer({ id: clusterSourceId, enabled: true });
  console.log(`[map] Clustering enabled for ${points.length} assets (GeoJSON)`);
}
